import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Button, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import Spinner from "../generics/Spinner";

const MACHINE_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "MAINTENANCE", label: "Under Maintenance" },
  { value: "RETIRED", label: "Retired" },
];

const MachineForm = ({
  machine,
  machineTypes,
  locations,
  loading,
  error,
  onSubmit,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    machine_type: "",
    location: "",
    serial_number: "",
    model_number: "",
    manufacturer: "",
    purchase_date: "",
    installation_date: "",
    warranty_expiration: "",
    maintenance_frequency_days: "",
    status: "ACTIVE",
    description: "",
    notes: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (machine && isEdit) {
      setFormData({
        name: machine.name || "",
        machine_type: machine.machine_type || "",
        location: machine.location || "",
        serial_number: machine.serial_number || "",
        model_number: machine.model_number || "",
        manufacturer: machine.manufacturer || "",
        purchase_date: machine.purchase_date || "",
        installation_date: machine.installation_date || "",
        warranty_expiration: machine.warranty_expiration || "",
        maintenance_frequency_days: machine.maintenance_frequency_days || "",
        status: machine.status || "ACTIVE",
        description: machine.description || "",
        notes: machine.notes || "",
      });
      if (machine.photo) {
        setPhotoPreview(machine.photo);
      }
    }
  }, [machine, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Create FormData if we have a photo
    let submitData;
    if (photoFile) {
      submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== "" && formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });
      submitData.append("photo", photoFile);
    } else {
      // Clean up empty fields
      submitData = {};
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== "" && formData[key] !== null) {
          submitData[key] = formData[key];
        }
      });
    }

    if (onSubmit) {
      onSubmit(submitData);
    }
  };

  if (loading && isEdit && !machine) {
    return <Spinner />;
  }

  return (
    <div className="machine-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fa fa-cog me-2"></i>
          {isEdit ? "Edit Machine" : "Add New Machine"}
        </h2>
        <Link to="/maintenance/machines" className="btn btn-outline-secondary">
          <i className="fa fa-arrow-left me-1"></i> Back
        </Link>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {typeof error === "object"
            ? Object.entries(error).map(([key, val]) => (
                <div key={key}><strong>{key}:</strong> {Array.isArray(val) ? val.join(", ") : val}</div>
              ))
            : error.message || "An error occurred"}
        </Alert>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <i className="fa fa-info-circle me-2"></i>
                Basic Information
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Machine Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Main Kitchen AC Unit #1"
                      />
                      <Form.Control.Feedback type="invalid">
                        Machine name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Machine Type *</Form.Label>
                      <Form.Select
                        name="machine_type"
                        value={formData.machine_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {machineTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Machine type is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location *</Form.Label>
                      <Select
                        options={locations.map((loc) => ({
                          value: loc.id,
                          label: loc.name,
                        }))}
                        value={
                          formData.location
                            ? {
                                value: formData.location,
                                label: locations.find((l) => l.id === formData.location)?.name || "",
                              }
                            : null
                        }
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            location: selected ? selected.value : "",
                          }))
                        }
                        placeholder="Search and select location..."
                        isClearable
                        isSearchable
                        classNamePrefix="react-select"
                      />
                      {validated && !formData.location && (
                        <div className="text-danger small mt-1">
                          Location is required
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Serial Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="serial_number"
                        value={formData.serial_number}
                        onChange={handleChange}
                        placeholder="e.g., AC-2021-001"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Model Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="model_number"
                        value={formData.model_number}
                        onChange={handleChange}
                        placeholder="e.g., Carrier 50XC"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Manufacturer</Form.Label>
                      <Form.Control
                        type="text"
                        name="manufacturer"
                        value={formData.manufacturer}
                        onChange={handleChange}
                        placeholder="e.g., Carrier"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Brief description of the machine"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Any additional notes or maintenance tips"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <i className="fa fa-calendar me-2"></i>
                Dates & Maintenance
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="purchase_date"
                        value={formData.purchase_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Installation Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="installation_date"
                        value={formData.installation_date}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Warranty Expiration</Form.Label>
                      <Form.Control
                        type="date"
                        name="warranty_expiration"
                        value={formData.warranty_expiration}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Maintenance Frequency (Days)</Form.Label>
                      <Form.Control
                        type="number"
                        name="maintenance_frequency_days"
                        value={formData.maintenance_frequency_days}
                        onChange={handleChange}
                        min="1"
                        placeholder="e.g., 90 for quarterly"
                      />
                      <Form.Text className="text-muted">
                        How often this machine should be maintained
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        {MACHINE_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header>
                <i className="fa fa-camera me-2"></i>
                Photo
              </Card.Header>
              <Card.Body>
                {photoPreview && (
                  <div className="mb-3">
                    <img
                      src={photoPreview}
                      alt="Machine preview"
                      className="img-fluid rounded"
                      style={{ maxHeight: "200px", width: "100%", objectFit: "cover" }}
                    />
                  </div>
                )}
                <Form.Group>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                  <Form.Text className="text-muted">
                    Upload a photo of the machine
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-2"></i>
                      {isEdit ? "Update Machine" : "Create Machine"}
                    </>
                  )}
                </Button>
                <Link to="/maintenance/machines" className="btn btn-outline-secondary">
                  Cancel
                </Link>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default MachineForm;
